import {Plural, Trans} from '@lingui/macro';
import React, {FC} from 'react';
import styled from 'styled-components';

import {Handler} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {ButtonVariantsEnum} from '../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {ButtonContent} from '../../../../../libs/shared/ui/src/components/buttons/buttonContent';
import {ChromelessButton} from '../../../../../libs/shared/ui/src/components/buttons/chromelessButton';
import {IconButton} from '../../../../../libs/shared/ui/src/components/buttons/iconButton';
import {Icon} from '../../../../../libs/shared/ui/src/components/icon/icon';
import {VisualSizesEnum} from '../../../../../libs/shared/ui/src/styles/commonStyles';
import {fontSizes, fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {PaletteColorsEnum} from '../../../../../libs/shared/ui/src/styles/paletteModel';
import {useUserTheme} from '../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {isJobRunning} from '../../../../helpers/jobHelpers';
import {CrmSyncModel, CrmSyncSourcesEnum} from '../../../../models/api/crmSyncModel';
import {SyncJobModel} from '../../../../models/api/jobModel';

/*
 * Props.
 */

interface CrmSyncErrorsBannerProps {
  className?: string;
  crmSync?: CrmSyncModel;
  shouldDisplayCrmSyncName?: boolean;
  latestJob?: SyncJobModel;
  onViewErrorsClick: Handler;
  onCloseClick?: Handler;
}

/*
 * Style.
 */

const StyledBannerWrapperDiv = styled.div`
  display: flex;
  flex-flow: row;
  flex-wrap: wrap;
  gap: 10px;
  background: ${(p) => p.theme.palette.orange.shade20};
  border-radius: 8px;
  padding: 9px 12px;
`;

const StyledTextWrapperDiv = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
  line-height: 16px;
  font-size: ${fontSizes.verySmall};
  font-weight: ${fontWeights.medium};
`;

const StyledChromelessButton = styled(ChromelessButton)`
  margin: -5px -6px -5px 0;
`;

const StyledButtonContent = styled(ButtonContent)`
  font-size: ${fontSizes.tiny};
  white-space: nowrap;
  border-radius: 13px;
`;

const StyledIconButton = styled(IconButton)`
  margin: -5px -6px -5px 0;
  border-radius: 50%;
`;

/*
 * Component.
 */

export const CrmSyncErrorsBanner: FC<CrmSyncErrorsBannerProps> = (props) => {
  const theme = useUserTheme();
  const {className, crmSync, shouldDisplayCrmSyncName, latestJob, onViewErrorsClick, onCloseClick} = props;

  if (
    !crmSync ||
    crmSync.errors.length === 0 ||
    !latestJob ||
    isJobRunning(latestJob) ||
    !latestJob.metadata?.syncFailed
  ) {
    return null;
  }
  return (
    <StyledBannerWrapperDiv className={className}>
      <Icon name="warningFilledSmall" color={theme.palette.orange.shade50} />
      <StyledTextWrapperDiv>
        {renderCrmSyncErrorsText(crmSync, latestJob.metadata.syncFailed, shouldDisplayCrmSyncName)}
      </StyledTextWrapperDiv>
      <StyledChromelessButton onClick={onViewErrorsClick}>
        <StyledButtonContent
          size={VisualSizesEnum.SMALL}
          variant={ButtonVariantsEnum.LINK}
          color={PaletteColorsEnum.BLUE}
        >
          <Trans id="crm-sync-errors-banner-view-errors-button">VIEW ERRORS</Trans>
        </StyledButtonContent>
      </StyledChromelessButton>
      {maybeRenderCloseButton(onCloseClick)}
    </StyledBannerWrapperDiv>
  );
};

/*
 * Helpers.
 */

function maybeRenderCloseButton(onCloseClick?: Handler) {
  if (!onCloseClick) {
    return null;
  }
  return <StyledIconButton iconName="closeSmall" onClick={onCloseClick} label={null} />;
}

function renderCrmSyncErrorsText(
  crmSync: CrmSyncModel,
  totalFailed: number,
  shouldDisplayCrmSyncName?: boolean,
) {
  if (!shouldDisplayCrmSyncName) {
    return renderDefaultCrmSyncErrors(totalFailed);
  }
  switch (crmSync.source) {
    case CrmSyncSourcesEnum.SALESFORCE:
      return (
        <Plural
          id="crm-sync-errors-banner-salesforce-total-errors"
          value={totalFailed}
          one="# Salesforce sync error found"
          other="# Salesforce sync errors found"
        />
      );
    case CrmSyncSourcesEnum.HUBSPOT:
      return (
        <Plural
          id="crm-sync-errors-banner-hubspot-total-errors"
          value={totalFailed}
          one="# Hubspot sync error found"
          other="# Hubspot sync errors found"
        />
      );
    case CrmSyncSourcesEnum.MICROSOFT_DYNAMICS:
      return (
        <Plural
          id="crm-sync-errors-banner-microsoft-dynamics-total-errors"
          value={totalFailed}
          one="# Dynamics 365 sync error found"
          other="# Dynamics 365 sync errors found"
        />
      );
    default:
      return renderDefaultCrmSyncErrors(totalFailed);
  }
}

function renderDefaultCrmSyncErrors(errorCount: number) {
  return (
    <Plural
      id="crm-sync-errors-banner-total-errors"
      value={errorCount}
      one="# sync error found"
      other="# sync errors found"
    />
  );
}
